/**
 * Legacy reports_cst_orders semantics — mirrors API_v5 LegacyOrderParity.php.
 */
import type { ReportFilterCatalog, ReportOptionSlot } from '@/types/customReports.types';

export const TYPES_SELECT1_BY_FAMILY: Record<string, string> = {
  Appointments: '1',
  Stages: '2',
  Conversions: '3',
  PlanConversions: '4',
  AppointmentConversions: '5',
  Payments: '7',
  AllPayments: '8',
  NonPlanPayments: '9',
  Surgeries: '11',
  Patients: '19',
  Memberships: '21',
  Members: '23',
};

export const HIDDEN_WIZARD_FAMILIES = new Set(['Surgeries', 'Payments', 'NonPlanPayments']);

export const REPORT_TIME_FRAME_TODAY = '31';

/** Legacy Overall label when totalType=2 and no sub-filters (admin_report_list.php:1921-1922). */
export const SUB_FILTERS_TEXT_COMBINED_TOTAL = 'Combined Total';

/** Full legacy reportTimeFrame enum (admin_report_list.php lines 4117–4153). */
export const REPORT_TIME_FRAMES: Array<{ value: string; label: string }> = [
  { value: '31', label: 'Today' },
  { value: '1', label: 'This Week-to-date' },
  { value: '34', label: 'This Month' },
  { value: '2', label: 'This Month-to-date' },
  { value: '11', label: 'This Quarter-to-date' },
  { value: '3', label: 'This Year-to-date' },
  { value: '35', label: 'All Time-to-date' },
  { value: '32', label: 'Yesterday' },
  { value: '12', label: 'Last Week' },
  { value: '13', label: 'Last Week-to-date' },
  { value: '14', label: 'Last Month' },
  { value: '15', label: 'Last Month-to-date' },
  { value: '16', label: 'Last Quarter' },
  { value: '17', label: 'Last Quarter-to-date' },
  { value: '18', label: 'Last Year' },
  { value: '19', label: 'Last Year-to-date' },
  { value: '4', label: 'Last 7 Days' },
  { value: '5', label: 'Last 14 Days' },
  { value: '6', label: 'Last 21 Days' },
  { value: '7', label: 'Last 30 Days' },
  { value: '8', label: 'Last 60 Days' },
  { value: '9', label: 'Last 90 Days' },
  { value: '10', label: 'Last 365 Days' },
  { value: '20', label: 'Next Week' },
  { value: '21', label: 'Next Month' },
  { value: '33', label: 'Month After Next' },
  { value: '22', label: 'Next Quarter' },
  { value: '23', label: 'Next Year' },
  { value: '24', label: 'Next 7 Days' },
  { value: '25', label: 'Next 14 Days' },
  { value: '26', label: 'Next 21 Days' },
  { value: '27', label: 'Next 30 Days' },
  { value: '28', label: 'Next 60 Days' },
  { value: '29', label: 'Next 90 Days' },
  { value: '30', label: 'Next 365 Days' },
];

/** Legacy reportIncrement (admin_report_list.php lines 4097–4104). */
export const REPORT_INCREMENTS: Array<{ value: string; label: string }> = [
  { value: '1', label: 'Daily' },
  { value: '2', label: 'Weekly' },
  { value: '5', label: 'Bi-Weekly' },
  { value: '3', label: 'Monthly' },
  { value: '6', label: 'Bi-Monthly' },
  { value: '4', label: 'Yearly' },
  { value: '7', label: 'Total' },
];

/**
 * Time frames that legacy upgrades a Daily increment to Weekly for.
 * admin_report_list.php:3844.
 */
const INCREMENT_UPGRADE_TO_WEEKLY = new Set(['15', '8', '9', '33', '21', '28', '29']);

/**
 * Time frames that legacy upgrades a Daily *or* Weekly increment to Monthly for.
 * admin_report_list.php:3847 and :3852 (the same list, checked twice).
 */
const INCREMENT_UPGRADE_TO_MONTHLY = new Set([
  '11', '16', '17', '22', '30', '19', '18', '3', '23',
]);

/**
 * Port of legacy `adjustIncrement(tfValue)` (admin_report_list.php:3839-3856).
 *
 * Legacy fires this from the reportTimeFrame `<select onchange>` (:4117) only —
 * so picking "This Year-to-date" silently upgrades Daily to Monthly, but a user
 * who then *deliberately* picks Daily keeps it. That asymmetry is the behaviour,
 * not a bug: reproduce it by calling this only when the time frame changes, never
 * when the increment itself changes, and never server-side (which would override
 * a deliberate choice legacy allows).
 *
 * Only ever upgrades (Daily→Weekly→Monthly, or anything→Total for All Time-to-date).
 */
export function adjustIncrementForTimeFrame(
  timeFrame: string,
  currentIncrement: string,
): string {
  // All Time-to-date overrides whatever is selected.
  if (timeFrame === '35') {
    return '7';
  }

  let increment = currentIncrement;
  if (increment === '1') {
    if (INCREMENT_UPGRADE_TO_WEEKLY.has(timeFrame)) {
      increment = '2';
    }
    if (INCREMENT_UPGRADE_TO_MONTHLY.has(timeFrame)) {
      increment = '3';
    }
  }
  if (increment === '2' && INCREMENT_UPGRADE_TO_MONTHLY.has(timeFrame)) {
    increment = '3';
  }

  return increment;
}

export const REPORT_PROJECTIONS: Array<{ value: string; label: string }> = [
  { value: '1', label: 'Off' },
  { value: '2', label: 'On' },
];

export const CHART_OUTPUT_TYPES: Array<{ value: string; label: string }> = [
  { value: '1', label: 'Table' },
  { value: '2', label: 'Line Chart' },
  { value: '3', label: 'Area Chart' },
  { value: '4', label: 'Bar Chart' },
  { value: '5', label: 'Column Chart' },
];

export const SPREADSHEET_TYPE_SLUGS: Array<{ value: string; label: string; id: string }> = [
  { value: 'patients', label: 'Patients', id: '1' },
  { value: 'plans', label: 'Plans', id: '2' },
  { value: 'payments', label: 'Payments', id: '3' },
  { value: 'paymentproducts', label: 'Payment Products', id: '12' },
  { value: 'appointments', label: 'Appointments', id: '7' },
  { value: 'procedures', label: 'Quote Procedures', id: '5' },
  { value: 'materials', label: 'Quote Products', id: '6' },
  { value: 'memberships', label: 'Memberships', id: '8' },
  { value: 'memberss', label: 'Members', id: '14' },
  { value: 'patientnotes', label: 'Patient Notes', id: '11' },
  // 'labs' (id 10) is deliberately absent. Legacy's dropdown offers it
  // (admin_report_list.php:4174-4194) but tags no column rows with it
  // (:4197-4327), so an FTP labs spreadsheet comes out blank; and id 10 is not in
  // SPREADSHEET_LABEL_BY_ID, so OrderRules rejects it with a 422 that surfaces as a
  // bare "Save failed". Offering it is strictly worse than omitting it.
  { value: 'tags', label: 'Tags', id: '13' },
];

export const RECURRING_VALUES: Array<{ value: string; label: string }> = [
  { value: '0', label: 'One time' },
  { value: '1', label: 'Recurring' },
];

export const DELIVER_EVERY_VALUES: Array<{ value: string; label: string }> = [
  { value: '1 day', label: 'Day' },
  { value: '2 days', label: '2 Days' },
  { value: '3 days', label: '3 Days' },
  { value: '4 days', label: '4 Days' },
  { value: '5 days', label: '5 Days' },
  { value: '6 days', label: '6 Days' },
  { value: '7 days', label: '7 Days' },
  { value: '2 weeks', label: '2 Weeks' },
  { value: '3 weeks', label: '3 Weeks' },
  { value: '1 month', label: 'Month' },
  { value: '2 months', label: '2 Months' },
  { value: '3 months', label: '3 Months' },
  { value: '4 months', label: '4 Months' },
  { value: '5 months', label: '5 Months' },
  { value: '6 months', label: '6 Months' },
  { value: '7 months', label: '7 Months' },
  { value: '8 months', label: '8 Months' },
  { value: '9 months', label: '9 Months' },
  { value: '10 months', label: '10 Months' },
  { value: '11 months', label: '11 Months' },
  { value: '1 year', label: 'Year' },
];

export const SUB_FILTER_DIMENSION_BY_KEY: Record<string, string> = {
  coordinators: '1',
  nurses: '2',
  doctors: '3',
  receptionists: '4',
  facilities: '5',
  procedureCategories: '6',
  procedureSubCategories: '7',
  referralCategories: '8',
  referralSubCategories: '9',
  stages: '10',
  apptTypeGroups: '11',
  apptTypes: '12',
  merchants: '13',
  languages: '14',
  tags: '15',
  zipCode1: '16',
  zipCode2: '17',
  gender: '18',
  leadSources: '19',
  planTypes: '20',
  interestLevel: '21',
  planStatuses: '22',
  procedureCount: '23',
  fees: '24',
  discounts: '25',
  memberships: '26',
  products: '27',
  packages: '28',
  cancelReasons: '29',
  creators: '30',
  apptResources: '31',
};

const COLOR_PALETTE = [
  '0d3a8b', '8b1f0d', '8b450d', '105c0e', '280e5c', '5c0e24', '5c5b0e', '0c6663', '1840d8', 'd82a18',
  '002d60', '864f00', 'b28e00', '134f00', '7c007d', '8c0000', '2e5800', '00516b', '0f1c33', '301100',
  '0d3a8b', '8b1f0d', '8b450d', '105c0e', '280e5c', '5c0e24', '5c5b0e', '0c6663', '1840d8', 'd82a18',
  '002d60', '864f00', 'b28e00', '134f00', '7c007d', '8c0000', '2e5800', '00516b', '0f1c33', '301100',
  '0d3a8b', '8b1f0d', '8b450d', '105c0e', '280e5c', '5c0e24', '5c5b0e', '0c6663', '1840d8', 'd82a18',
  '002d60', '864f00', 'b28e00', '134f00', '7c007d', '8c0000', '2e5800', '00516b', '0f1c33', '301100',
  '0d3a8b', '8b1f0d', '8b450d', '105c0e', '280e5c', '5c0e24', '5c5b0e', '0c6663', '1840d8', 'd82a18',
  '002d60', '864f00', 'b28e00', '134f00', '7c007d', '8c0000', '2e5800', '00516b', '0f1c33', '301100',
];

export const REPORT_TITLE_BY_FAMILY: Record<string, string> = {
  Appointments: 'Appointments Scheduled',
  Stages: 'Stage Changes',
  Conversions: 'Stage Conversions',
  PlanConversions: 'Plan Conversions',
  AppointmentConversions: 'Appointment Conversions',
  AllPayments: 'Payments',
  Patients: 'Patients',
  Memberships: 'Memberships',
  Members: 'Members',
};

/**
 * Legacy `reportSubTitle` per family + `typesSelect2`, transcribed from
 * `loadReport()` in admin_report_list.php (the block at 1000-1845 — note the
 * near-identical copy in `checkForErrors()` around 2611-3251 assigns globals and
 * never reaches persistence, so it is not authoritative).
 *
 * The cron copies this column verbatim into `reports_cst_results`
 * (report_generator_autorun_pbi.php:5109 et al) and it becomes part of the report
 * heading — `admin_report_display3.php:30-34` renders "<title> - <subtitle>", the
 * dashboard tile renders "<title> (<subtitle>)". Leaving it blank is why V5-created
 * reports read "Payments" where FTP reads "Payments - Amount".
 *
 * Gaps are deliberate and match legacy: Payments type 4 and AllPayments type 6 (both
 * "Discounts") have no `reportSubTitle` case, so legacy stores '' for them too.
 */
export const REPORT_SUBTITLE_BY_FAMILY: Record<string, Record<string, string>> = {
  // admin_report_list.php:1003-1009
  Appointments: { '1': 'Activity', '2': 'Percent of Total', '3': 'Non-activity' },
  // :1217-1229
  Stages: {
    '1': 'Activity',
    '2': 'Percent of Total',
    '3': 'Total Procedure Fee',
    '4': 'Maximum Procedure Fee',
    '5': 'Minimum Procedure Fee',
  },
  // :1307-1312
  Conversions: { '2': 'Activity', '1': 'Rate', '3': 'Average Days to Conversion' },
  // :1449-1455
  PlanConversions: { '2': 'Activity', '1': 'Rate', '3': 'Average Days to Conversion' },
  // :1496-1502
  AppointmentConversions: { '2': 'Activity', '1': 'Rate', '3': 'Average Time to Conversion' },
  // :1617-1623 — no case for type 4 (Discounts)
  Payments: { '1': 'Amount', '2': 'Percent of Total', '3': 'Maximum Payment' },
  // :1630-1642 — no case for type 6 (Discounts)
  AllPayments: {
    '1': 'Amount',
    '2': 'Percent of Total',
    '3': 'Maximum Payment',
    '4': 'Charged Transaction',
    '5': 'Scheduled Recurrence',
  },
  // :1649
  NonPlanPayments: { '1': 'Amount' },
  // :1738-1744
  Surgeries: { '1': 'Activity', '2': 'Procedures', '3': 'Estimated Hours' },
  // :1770-1776
  Patients: { '1': 'Date Added', '2': 'Date of Birth', '3': 'Birthdays' },
  // :1806-1830
  Memberships: {
    '1': 'Created',
    '2': 'Cancelled',
    '3': 'Billed',
    '4': 'Did not bill',
    '5': 'Declined',
    '6': 'Settled',
    '7': 'Declined but settled later',
    '8': 'Existed during',
    '9': 'Existed by end',
  },
  // :1837-1843
  Members: { '1': 'Active', '2': 'Started', '3': 'Cancelled' },
};

/** Legacy `reportSubTitle` for a family + typesSelect2, '' when legacy has no case. */
export function reportSubTitleFor(reportSelect: string, typesSelect2: string): string {
  return REPORT_SUBTITLE_BY_FAMILY[reportSelect]?.[typesSelect2] ?? '';
}

export function typesSelect1ForFamily(reportSelect: string): string {
  return TYPES_SELECT1_BY_FAMILY[reportSelect] ?? '0';
}

export function defaultColorArray(): string {
  return COLOR_PALETTE.join(',');
}

export function sanitizeEmailAddresses(raw: string): string {
  return raw.replace(/[^\w@.,+\-\s]/g, '');
}

export function encodeAudienceBucket(checked: boolean): string {
  return checked ? '1' : '0';
}

/** The audience columns are `int unsigned`, so the API sends `1`, not `'1'`. */
export function parseAudienceBucket(raw: string | number | null | undefined): boolean {
  const value = raw == null ? '' : String(raw);
  return value === '1' || value === 'true';
}

/** Reverse map: dim id (string) -> group key. Built from SUB_FILTER_DIMENSION_BY_KEY. */
export const SUB_FILTER_KEY_BY_DIMENSION: Record<string, string> = Object.fromEntries(
  Object.entries(SUB_FILTER_DIMENSION_BY_KEY).map(([k, v]) => [v, k]),
);

/** Order-stable list of group keys, sorted by dimension id (matches legacy admin_report_order3.php). */
export const SUB_FILTER_GROUPS_IN_ORDER: string[] = Object.entries(SUB_FILTER_DIMENSION_BY_KEY)
  .sort((a, b) => Number(a[1]) - Number(b[1]))
  .map(([k]) => k);

export interface SubFilterSelection {
  /** IDs as legacy stores them — negative means "Current" assignment for people. */
  ids: string[];
  /** Human labels matched 1:1 with `ids` order (the " (Current)" suffix is included for negative IDs). */
  labels: string[];
  /** True when this dimension's "Apply to All" checkbox is set. */
  applyToAll?: boolean;
}

export type SubFilterSelections = Record<string, SubFilterSelection>;

/**
 * Encode legacy `subFilters` / `subFiltersText` / `filterText` columns from
 * structured wizard selections.
 *
 * Format (per `admin_report_order3.php` 385-697 + `admin_report_list.php` 1855-1928):
 *   subFilters     -> `dim[!]|id1,id2;` per dim with selections (`!` if Apply-to-All)
 *   subFiltersText -> `label;label;...` flat list of labels across all dims (Individual)
 *                     OR `Combined Total` for Overall / no selections
 *   filterText     -> `dim!label1[^]|label2[^]|;` per dim (`^` between label and `|`
 *                     when Apply-to-All is set on the dim)
 *
 * Iteration order matches legacy: dim id ascending (1, 2, 3, ... 31).
 */
export function encodeSubFilters(
  selections: SubFilterSelections,
): { subFilters: string; subFiltersText: string; filterText: string } {
  let subFilters = '';
  let subFiltersText = '';
  let filterText = '';

  for (const groupKey of SUB_FILTER_GROUPS_IN_ORDER) {
    const selection = selections[groupKey];
    if (!selection) continue;
    const dimId = SUB_FILTER_DIMENSION_BY_KEY[groupKey];
    if (!dimId) continue;

    const idsClean = selection.ids.filter((id) => id !== '' && id !== '0' && id != null);
    if (idsClean.length === 0) continue;

    const isOverall = Boolean(selection.applyToAll);

    subFilters += dimId;
    if (isOverall) subFilters += '!';
    subFilters += '|' + idsClean.join(',') + ';';

    let dimFilterText = '';
    for (const label of selection.labels) {
      const trimmed = (label ?? '').replace(/;/g, '').trim();
      if (!trimmed) continue;
      subFiltersText += trimmed + ';';
      dimFilterText += trimmed;
      if (isOverall) dimFilterText += '^';
      dimFilterText += '|';
    }
    if (dimFilterText !== '') {
      filterText += dimId + '!' + dimFilterText + ';';
    }
  }

  return { subFilters, subFiltersText, filterText };
}

/**
 * Expand wizard composite groups (`zipRange`, `feesDiscounts`) into the
 * legacy atomic dim selections the encoder iterates over.
 *
 *   zipRange       { ids: [from, to] }                 -> zipCode1 { [from] } + zipCode2 { [to] }
 *   feesDiscounts  { ids: ['F<n>', 'D<n>'] }           -> fees { [n] } + discounts { [n] }
 *
 * Other group keys pass through unchanged.
 */
export function expandCompositeSelections(input: SubFilterSelections): SubFilterSelections {
  const out: SubFilterSelections = { ...input };

  const zip = input.zipRange;
  if (zip && zip.ids.length === 2 && zip.ids[0] !== '' && zip.ids[1] !== '') {
    out.zipCode1 = { ids: [zip.ids[0]], labels: ['Zip Code'], applyToAll: zip.applyToAll };
    out.zipCode2 = { ids: [zip.ids[1]], labels: [], applyToAll: zip.applyToAll };
  }
  delete out.zipRange;

  const fd = input.feesDiscounts;
  if (fd && fd.ids.length > 0) {
    const feesId = fd.ids.find((id) => id.startsWith('F'))?.slice(1);
    const discId = fd.ids.find((id) => id.startsWith('D'))?.slice(1);
    if (feesId) out.fees = { ids: [feesId], labels: ['Fees'], applyToAll: fd.applyToAll };
    if (discId) out.discounts = { ids: [discId], labels: ['Discounts'], applyToAll: fd.applyToAll };
  }
  delete out.feesDiscounts;

  return out;
}

/**
 * Build the trio of legacy text columns for a wizard order.
 *
 * Overall (totalType=2) or no selections -> `subFiltersText='Combined Total'`,
 * but `subFilters`/`filterText` are still encoded from selections so the
 * legacy cron's filter SQL is honored either way (matches admin_report_list.php
 * lines 1921-1927: legacy keeps building `subFiltersString` regardless of
 * totalType and only the human-label cache differs).
 */
export function buildSubFilterPayload(
  totalType: '1' | '2',
  selections: SubFilterSelections,
): { subFilters: string; subFiltersText: string; filterText: string } {
  const encoded = encodeSubFilters(expandCompositeSelections(selections));
  const hasSelections = encoded.subFilters !== '';

  if (totalType === '2' || !hasSelections) {
    return {
      subFilters: encoded.subFilters,
      subFiltersText: SUB_FILTERS_TEXT_COMBINED_TOTAL,
      filterText: encoded.filterText,
    };
  }

  return encoded;
}

/**
 * Inverse of `expandCompositeSelections`: collapse atomic zipCode1/zipCode2
 * and fees/discounts dims back into the wizard's composite group keys so
 * the UI controls re-populate correctly when editing.
 */
export function collapseCompositeSelections(input: SubFilterSelections): SubFilterSelections {
  const out: SubFilterSelections = { ...input };

  const z1 = input.zipCode1;
  const z2 = input.zipCode2;
  if (z1 || z2) {
    const from = z1?.ids[0] ?? '';
    const to = z2?.ids[0] ?? '';
    if (from || to) {
      out.zipRange = {
        ids: [from, to],
        labels: ['Zip Code'],
        applyToAll: z1?.applyToAll || z2?.applyToAll,
      };
    }
    delete out.zipCode1;
    delete out.zipCode2;
  }

  const fees = input.fees;
  const discounts = input.discounts;
  if (fees || discounts) {
    const ids: string[] = [];
    const labels: string[] = [];
    if (fees && fees.ids[0]) {
      ids.push('F' + fees.ids[0]);
      labels.push('Fees');
    }
    if (discounts && discounts.ids[0]) {
      ids.push('D' + discounts.ids[0]);
      labels.push('Discounts');
    }
    if (ids.length > 0) {
      out.feesDiscounts = {
        ids,
        labels,
        applyToAll: fees?.applyToAll || discounts?.applyToAll,
      };
    }
    delete out.fees;
    delete out.discounts;
  }

  return out;
}

/**
 * Decode legacy `subFilters` / `subFiltersText` / `filterText` columns back
 * into wizard `SubFilterSelections`. Faithful inverse of `encodeSubFilters`,
 * mirroring `App\Services\Reports\SubFilterParser` on the PHP side.
 *
 * The wizard composite groups (zipRange/feesDiscounts) are **not** collapsed
 * here — call `collapseCompositeSelections()` after if you want UI shapes.
 */
export function decodeSubFilters(
  subFilters: string | null | undefined,
  filterText: string | null | undefined,
): SubFilterSelections {
  const out: SubFilterSelections = {};

  // First pass: ids + applyToAll markers from `subFilters`.
  if (subFilters && subFilters.trim() !== '') {
    for (const segment of subFilters.split(';')) {
      const seg = segment.trim();
      if (seg === '') continue;
      const pipeIdx = seg.indexOf('|');
      if (pipeIdx === -1) continue;
      let rawDim = seg.slice(0, pipeIdx);
      const csv = seg.slice(pipeIdx + 1);

      let applyToAll = false;
      if (rawDim.includes('!')) {
        applyToAll = true;
        rawDim = rawDim.replace(/!/g, '');
      }
      if (!/^-?\d+$/.test(rawDim)) continue;

      const dimId = rawDim;
      const groupKey = SUB_FILTER_KEY_BY_DIMENSION[dimId];
      if (!groupKey) continue;

      const ids = csv
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s !== '');

      out[groupKey] = { ids, labels: [], applyToAll: applyToAll || undefined };
    }
  }

  // Second pass: labels from `filterText` (`dim!label1[^]|label2[^]|;`).
  if (filterText && filterText.trim() !== '') {
    for (const segment of filterText.split(';')) {
      const seg = segment.trim();
      if (seg === '') continue;
      const bangIdx = seg.indexOf('!');
      if (bangIdx === -1) continue;
      const rawDim = seg.slice(0, bangIdx);
      const labelBlob = seg.slice(bangIdx + 1);
      if (!/^\d+$/.test(rawDim)) continue;
      const dimId = rawDim;
      const groupKey = SUB_FILTER_KEY_BY_DIMENSION[dimId];
      if (!groupKey) continue;

      // Strip a trailing | so `a|b|` -> ['a','b'] not ['a','b',''].
      const cleaned = labelBlob.endsWith('|') ? labelBlob.slice(0, -1) : labelBlob;
      const labels = cleaned
        .split('|')
        .map((label) => label.replace(/\^$/g, '').trim())
        .filter((label) => label !== '');

      const existing = out[groupKey] ?? { ids: [], labels: [] };
      out[groupKey] = { ...existing, labels };
    }
  }

  return out;
}

export function buildDisplayMetadata(
  reportSelect: string,
  title: string,
  typesSelect2 = '',
) {
  return {
    reportTitle: REPORT_TITLE_BY_FAMILY[reportSelect] ?? (title || reportSelect),
    reportSubTitle: reportSubTitleFor(reportSelect, typesSelect2),
    // Legacy builds this from account-specific stage / appointment-type / survey-question
    // names (e.g. admin_report_list.php:1237 "<strong>Stage:</strong> " + stageTitleObject[...]),
    // so it needs the same catalogs the Filters step loads. Still unported.
    reportOptionsTitle: '',
    colorArray: defaultColorArray(),
  };
}

export function spreadsheetTypeIdFromSlug(slug: string): string {
  const match = SPREADSHEET_TYPE_SLUGS.find((s) => s.value === slug);
  return match?.id ?? '0';
}

// ─── Filters-step option slots ───────────────────────────────────────────────
//
// ReportFamilyRegistry::slot() carries no optional flag, so every select slot the
// wizard renders is mandatory. That makes "answered" mean *the stored value is one of
// this slot's choices* — not merely "non-empty".
//
// The distinction is the whole bug: buildPayload persists an untouched slot as the
// string '0' for legacy parity, but none of the conversion-metric ('2','1','3'),
// appointment-milestone ('1'–'11') or timeframe ('1'–'8') choice lists contains a '0'.
// A plain `Boolean(value)` test accepts that '0' while the <select> shows no selection,
// so an order could round-trip into a state where every visible dropdown looked filled
// in and Continue was disabled anyway, naming no field.

/** Slots with a choice list of their own are the only ones the wizard can require. */
export function isRequiredChoiceSlot(slot: ReportOptionSlot | null | undefined): boolean {
  return Boolean(slot && slot.type === 'select' && slot.choices.length > 0);
}

/**
 * Whether `value` is something this slot can actually represent. Free-text and
 * empty-choice slots accept anything (including ''); a select is satisfied only by one
 * of its own choice values.
 */
export function slotAcceptsValue(
  slot: ReportOptionSlot | null | undefined,
  value: string | null | undefined,
): boolean {
  if (!isRequiredChoiceSlot(slot)) return true;
  const candidate = value == null ? '' : String(value);
  return slot!.choices.some((choice) => choice.value === candidate);
}

/**
 * Stored column value → draft value for one slot. Anything the slot cannot display —
 * legacy's '0' sentinel, or an id since retired from the catalog — collapses to '' so
 * the placeholder the user sees and the validation that gates Continue agree.
 */
export function normalizeStoredSlotValue(
  slot: ReportOptionSlot | null | undefined,
  stored: string | null | undefined,
): string {
  const value = stored == null ? '' : String(stored);
  if (!isRequiredChoiceSlot(slot)) return value;
  return slotAcceptsValue(slot, value) ? value : '';
}

/** Every slot the Filters step renders, in display order. */
export function filterCatalogSlots(
  catalog: ReportFilterCatalog | null | undefined,
): ReportOptionSlot[] {
  if (!catalog) return [];
  return [catalog.subtype1, catalog.subtype2, ...catalog.options].filter(
    (slot): slot is ReportOptionSlot =>
      // A select with no choices renders as free text, so it stays visible; a null
      // slot is a family that does not use that position at all.
      Boolean(slot) && !(slot!.type === 'select' && slot!.choices.length === 0),
  );
}

/**
 * The Filters-step slots still waiting on an answer. Both the step's own message and
 * the Review step's blocker list derive from this, so the two cannot disagree about
 * what is missing.
 */
export function unsatisfiedSlots(
  catalog: ReportFilterCatalog | null | undefined,
  valueFor: (slotKey: string) => string | null | undefined,
): ReportOptionSlot[] {
  return filterCatalogSlots(catalog).filter(
    (slot) => !slotAcceptsValue(slot, valueFor(slot.slot)),
  );
}

/** "Metric", "Metric and Initial Milestone", "A, B and C" — for one-line prompts. */
export function formatFieldList(labels: string[]): string {
  if (labels.length === 0) return '';
  if (labels.length === 1) return labels[0];
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}
